import React, { Suspense, useMemo } from 'react';
import { ButtonGroup, Chip } from '@mui/material';
import Button from '@components/Atoms/Button';
import Typography from '@components/Atoms/Typography';
import ShowIf from '@components/Atoms/ShowIf';
import Box from '@components/Atoms/Box';
import Paper from '@components/Atoms/Paper';
import { useParams } from 'react-router-dom';
import Timer from '@components/Atoms/Timer';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ActionRequiredWrapper from '@contexts/ActionRequired/ActionRequiredWrapper';
import DateDisplay from '@components/Atoms/DateDisplay';
import EditIcon from '@mui/icons-material/Edit';
import {
  addMinutesToDate,
  formatDateWithTimezone,
  isValidDate,
  hasCalendarDateStarted,
  parseToLocalizedDate,
} from '@utils/datetime';
import { useUser } from '@reducers/user/hooks';
import { LockStrategy, Testlet, TestletStatus } from '../../types';
import {
  useGetActiveTestSession,
  useGetTestlets,
  useNavigateToLETestlet,
  useTestletGroupedByName,
} from '../../hooks';
import { useExamActionConfirmationDialog } from '../../contexts/ConfirmationDialogProvider';
import SessionManagementToolbar from '../SessionManagementToolbar';
import TestletStudentSessions from '../TestletStudentSessions';

const TeacherTestletView = () => {
  const testSession = useGetActiveTestSession();
  const {
    showLockConfirmationDialog,
    showUnlockConfirmationDialog,
    showUpdateConfirmationDialog,
  } = useExamActionConfirmationDialog();
  const testletsGroupedByName = useTestletGroupedByName();
  const params = useParams();
  const navigateToLearnosityExam = useNavigateToLETestlet();
  const { data: testlets } = useGetTestlets(params.testSessionId);

  const selectedTestlet = useMemo(
    () =>
      testlets?.find((testlet) => testlet.id === parseInt(params.testletId, 10)) ??
      ({} as Testlet),
    [testlets, params.testletId],
  );


  const isUnlocked = selectedTestlet?.status === TestletStatus.UNLOCKED;
  const isOverStartDate = useMemo(
    () => !!(testSession?.start_date && hasCalendarDateStarted(testSession.start_date)),
    [testSession],
  );
  const hasPreviousTestletNotLocked = useMemo(() => {
    let previousTestletNotLocked: (Testlet & { testSessionId: number }) | undefined;
    let isCurrentTestletFirst = false;

    testletsGroupedByName[selectedTestlet.name]?.forEach((testlet) => {
      if (isCurrentTestletFirst) {
        return;
      }

      if (
        testlet.id !== selectedTestlet.id &&
        testlet.status !== TestletStatus.LOCKED &&
        testlet.status !== TestletStatus.COMPLETED
      ) {
        previousTestletNotLocked = testlet;
      }
      if (testlet.id === selectedTestlet.id) {
        isCurrentTestletFirst = true;
      }
    });
    return previousTestletNotLocked;
  }, [testletsGroupedByName, selectedTestlet]);

  const getLocalizedLockDate = (unlockedAt, duration) => {
    if (!unlockedAt || typeof duration !== 'number' || duration <= 0) {
      return '';
    }
    const unlockDateLocalized = parseToLocalizedDate(unlockedAt);
    if (!unlockDateLocalized || !isValidDate(unlockDateLocalized)) {
      return '';
    }
    const lockDate = addMinutesToDate(unlockDateLocalized, duration);
    return formatDateWithTimezone(lockDate);
  };

  const statusLabel = useMemo(() => {
    switch (selectedTestlet?.status) {
      case TestletStatus.COMPLETED:
        return {
          text: 'COMPLETED: Students sessions have been scored.',
          color: 'primary',
        };
      case TestletStatus.NOT_STARTED:
      case TestletStatus.LOCKED:
        return {
          text: 'LOCKED: Students cannot take the test.',
          color: 'error',
        };
      case TestletStatus.UNLOCKED:
        return {
          text: 'UNLOCKED: Students can take the test.',
          color: 'success',
        };
      default:
        return {
          text: 'Loading Test Status...',
          color: 'info',
        };
    }
  }, [selectedTestlet?.status]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
        {/* Test Level Controls */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Z-Pattern Layout */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 3,
              }}
            >
              {/* Top Left - Test Name */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h3" component="h1" fontSize={24}>
                  {selectedTestlet?.name}
                </Typography>
              </Box>

              {/* Top Right - Status */}
              <Chip
                icon={
                  isUnlocked ? (
                    <LockOpenIcon sx={{ fontSize: '16px' }} />
                  ) : (
                    <LockIcon sx={{ fontSize: '16px' }} />
                  )
                }
                label={statusLabel.text}
                color={statusLabel.color}
                sx={{
                  fontWeight: 'bold',
                  height: 32,
                  pl: '8px',
                  alignSelf: 'start',
                }}
              />

              {/* Bottom Left - Test Info */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    component="p"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    Duration :
                  </Typography>
                  <Typography variant="subtitle1" component="p">
                    {selectedTestlet?.duration} minutes
                  </Typography>
                </Box>
                <ShowIf If={selectedTestlet?.status !== TestletStatus.NOT_STARTED}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      component="p"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      {isUnlocked ? 'Unlocked At :' : 'Locked At :'}
                    </Typography>
                    <Typography variant="subtitle1" component="p">
                      {isUnlocked
                        ? selectedTestlet?.unlocked_at && (
                            <DateDisplay
                              startDate={selectedTestlet?.unlocked_at}
                              variant="compact"
                              size="small"
                              showTime
                              color="subtitle"
                              showTimezone
                            />
                          )
                        : selectedTestlet?.locked_at && (
                            <DateDisplay
                              startDate={selectedTestlet?.locked_at}
                              variant="compact"
                              size="small"
                              showTime
                              color="subtitle"
                              showTimezone
                            />
                          )}
                    </Typography>
                  </Box>
                </ShowIf>
                <ShowIf If={isUnlocked}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      component="p"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      Auto Lock Timer :
                    </Typography>
                    <Typography variant="subtitle1" component="p">
                      <Timer
                        startTime={new Date(selectedTestlet?.unlocked_at ?? new Date())}
                        duration={(selectedTestlet?.auto_lock_duration ?? 0) * 60}
                        onTimeOut={() => {
                          console.log('time out');
                        }}
                      />
                    </Typography>
                    <ShowIf
                      If={
                        !!selectedTestlet?.unlocked_at &&
                        !!selectedTestlet?.auto_lock_duration
                      }
                    >
                      <Typography
                        variant="subtitle2"
                        component="p"
                        color="text.secondary"
                        sx={{ ml: 2 }}
                      >
                        Locks At :
                      </Typography>
                      <Typography variant="subtitle1" component="p">
                        <DateDisplay
                          startDate={getLocalizedLockDate(
                            selectedTestlet?.unlocked_at,
                            selectedTestlet?.auto_lock_duration,
                          )}
                          variant="compact"
                          size="small"
                          showTime
                          color="subtitle"
                          showTimezone
                        />
                      </Typography>
                    </ShowIf>
                    <Typography
                      variant="subtitle2"
                      component="p"
                      fontWeight="bold"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      On Lock :
                    </Typography>
                    <Typography variant="subtitle1" component="p">
                      {selectedTestlet?.lock_strategy === LockStrategy.USE_REMAINING_TIME 
                        ? 'Allowed to finish remaining time' 
                        : 'Paused immediately'}
                    </Typography>
                  </Box>
                </ShowIf>
              </Box>

              {/* Bottom Right - Test Controls */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                }}
              >
                <ShowIf If={isUnlocked}>
                  <ButtonGroup
                    variant="contained"
                    aria-label="Lock button with option to update testlet"
                  >
                    <Button
                      variant="contained"
                      onClick={() => showLockConfirmationDialog(selectedTestlet)}
                      sx={{ minWidth: 120 }}
                    >
                      Lock Test
                    </Button>
                    <Button
                      size="small"
                      onClick={() => showUpdateConfirmationDialog(selectedTestlet)}
                      title="Modify Test Settings"
                      aria-label="Modify Test Settings"
                    >
                      <EditIcon />
                    </Button>
                  </ButtonGroup>
                </ShowIf>
                <ShowIf If={!isUnlocked}>
                  <ActionRequiredWrapper
                    checkPath={['LE', +params.testSessionId, +selectedTestlet.id]}
                  >
                    <Button
                      variant="contained"
                      className="actionable"
                      onClick={() => showUnlockConfirmationDialog(selectedTestlet)}
                      sx={{ minWidth: 120 }}
                      disabled={!isOverStartDate || !!hasPreviousTestletNotLocked}
                    >
                      Unlock Test
                    </Button>
                  </ActionRequiredWrapper>
                </ShowIf>
              </Box>
            </Box>
            <ShowIf If={!isOverStartDate}>
              <Typography variant="subtitle1" component="p" color="error">
                You cannot unlock this test until the official start date.
              </Typography>
            </ShowIf>
            <ShowIf If={!!hasPreviousTestletNotLocked}>
              <Typography variant="subtitle1" component="p" color="error">
                You must complete the previous test before unlocking this one.
                <Button
                  variant="text"
                  onClick={() =>
                    hasPreviousTestletNotLocked &&
                    navigateToLearnosityExam({
                      testSessionId: String(hasPreviousTestletNotLocked.testSessionId),
                      testletId: String(hasPreviousTestletNotLocked.id),
                    })
                  }
                >
                  Go to Previous Testlet
                </Button>
              </Typography>
            </ShowIf>
          </Box>
        </Paper>
      </Box>
      {/* show list of student testlet sessions */}
      <SessionManagementToolbar>
        <TestletStudentSessions testlet={selectedTestlet} />
      </SessionManagementToolbar>
    </Suspense>
  );
};

export default TeacherTestletView;
